import { useEffect, useState } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import SearchableDropdown from './SearchableDropdown';
import { removeProvinceFromRegion, addProvinceToRegion, updateRegion } from '../services/project/regionService';
import { getProvinces } from '../services/project/projectService';
import { useUsers } from '../context/UserContext';

const AreaModal = ({ projectId, region, show, onClose, onRegionMutation }) => {
  const [provinces, setProvinces] = useState([]);
  const [regionName, setRegionName] = useState(region?.name ?? '');
  const [regionSortOrder, setRegionSortOrder] = useState(region?.sortOrder ?? '');
  const [savingName, setSavingName] = useState(false);
  const { isSuperAdmin } = useUsers();

  useEffect(() => {
    setRegionName(region?.name ?? '');
    setRegionSortOrder(region?.sortOrder ?? '');
  }, [region?.id, region?.name, region?.sortOrder]);

  useEffect(() => {
    const fetchProvince = async () => {
      try {
        const response = await getProvinces();
        setProvinces(response.provinces || []);
      } catch (error) {
        console.log(error);
      }
    };
    if (provinces.length === 0) fetchProvince();
  }, []); // load once

  const handleDelete = async (provinceId) => {
    try {
      const response = await removeProvinceFromRegion(projectId, region.id, provinceId);
      if (!response.success) {
        toast.error(response.data?.error || 'Failed to remove province');
        return;
      }
      onRegionMutation?.();
      toast.success(response.message || 'Province removed');
    } catch (error) {
      console.error('Error deleting area:', error);
      toast.error(error?.response?.data?.errorMessage || 'Something went wrong');
    }
  };

  const handleSubmit = async (provinceId) => {
    try {
      const response = await addProvinceToRegion(projectId, region.id, provinceId);
      if (!response?.success) {
        toast.error(response?.data?.error || 'Failed to add province');
        return;
      }
      onRegionMutation?.();
      toast.success(response.message || 'Province successfully added!');
    } catch (error) {
      console.error('Error adding area:', error);
      toast.error(error?.response?.data?.errorMessage || 'Something went wrong');
    }
  };

  const handleSaveRegion = async () => {
    const newName = regionName.trim();
    const newSortOrder = regionSortOrder ? parseInt(regionSortOrder) : null;
    
    if (!newName) {
      toast.error('Region name is required');
      return;
    }
    
    const updateData = {};
    if (newName !== (region?.name || '')) {
      updateData.name = newName;
    }
    if (newSortOrder !== region?.sortOrder) {
      updateData.sortOrder = newSortOrder;
    }
    
    if (Object.keys(updateData).length === 0) {
      toast.info('No changes to save');
      return;
    }
    
    try {
      setSavingName(true);
      const res = await updateRegion(projectId, region.id, updateData);
      if (!res?.success) {
        toast.error(res?.data?.error || 'Failed to update region');
        return;
      }
      toast.success(res.message || 'Region updated');
      onRegionMutation?.();
    } catch (error) {
      console.error('Update region error:', error);
      toast.error(error?.response?.data?.errorMessage || 'Something went wrong');
    } finally {
      setSavingName(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} fullscreen="md-down" centered>
      <Modal.Header closeButton>
        <Modal.Title>Edit Region</Modal.Title>
      </Modal.Header>

      <Modal.Body
        className="d-flex flex-column"
        style={{ height: '100%', maxHeight: '80vh', overflow: 'hidden' }}
      >
        <Form className="w-100">
          {/* Region name and sort order edit (separate update) */}
          { isSuperAdmin && (<Form.Group className="mb-3">
            <Form.Label>Region Name</Form.Label>
            <Form.Control
              type="text"
              value={regionName}
              onChange={e => setRegionName(e.target.value)}
              placeholder="Enter region name"
              className="mb-2"
            />
            <Form.Label>Sort Order</Form.Label>
            <div className="d-flex gap-2">
              <Form.Control
                type="number"
                value={regionSortOrder}
                onChange={e => setRegionSortOrder(e.target.value)}
                placeholder="Enter sort order (optional)"
              />
              <Button variant="primary" onClick={handleSaveRegion} disabled={savingName}>
                {savingName ? <Spinner size="sm" /> : 'Save'}
              </Button>
            </div>
          </Form.Group>) }

          {/* Provinces multi-select (add/remove) */}
          <SearchableDropdown
            name="provinceIds"
            label="Select Provinces"
            options={(provinces || []).map(p => ({ value: p.id, label: p.name }))}
            defaultValue={(region?.provinces || []).map(p => ({ value: p.id, label: p.name }))}
            isMulti
            onDelete={handleDelete}
            onSelect={handleSubmit}
          />
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default AreaModal;
